import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListPointsGroup } from 'src/features/parkingManagement/api/PointsGroup/useListPointsGroup.ts';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import PointsGroupFormModal from 'src/features/parkingManagement/components/PointsGroup/forms/PointsGroupFormModal';
import CellPointsGroupActions from 'src/features/parkingManagement/components/PointsGroup/PointsGroupActions';
import { type PointsGroup } from 'src/features/parkingManagement/types/pointsGroup.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { useListPoints } from 'src/features/points/api/useListPoints';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useTitleStore } from 'src/stores/useTitleStore';

function PointsGroupTable() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const {
        data: pointsGroupData,
        isLoading,
        isError,
        refetch,
    } = useListPointsGroup({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    const [pointsGroupsModalData, setPointsGroupsModalData] = React.useState<{
        selectedPointsGroup: null | PointsGroup;
        isDelete: boolean;
    }>({
        selectedPointsGroup: null,
        isDelete: false,
    });

    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });
    const { data: pointsData } = useListPoints({ parkingId: parkingId ?? '', config: { enabled: !!parkingId } });
    const allPoints = pointsData?.results ?? [];

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const { ...pagination } = usePagination();
    const { ...search } = useSearch();
    const columnHelper = createColumnHelper<PointsGroup>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(parkingManagementMessages.pointsGroupName),
            },
        }),
        columnHelper.accessor('points', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(parkingManagementMessages.pointsGroupPoints),
            },
            cell: ({ row }) => {
                const pointNames = row.original.points
                    .map((id) => allPoints.find((item) => item.id === id)?.name ?? null)
                    .filter((name): name is string => name !== null);
                return pointNames.length > 0 ? pointNames.join(', ') : '-';
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellPointsGroupActions
                    deleteCallback={() =>
                        setPointsGroupsModalData({ selectedPointsGroup: props.row.original, isDelete: true })
                    }
                    editCallback={() =>
                        setPointsGroupsModalData({ selectedPointsGroup: props.row.original, isDelete: false })
                    }
                    tooltipMessage={parkingManagementMessages.pointsGroup}
                    {...props}
                />
            ),
        }),
    ];

    const resetPointsGroupData = () => setPointsGroupsModalData({ selectedPointsGroup: null, isDelete: false });

    return (
        <>
            {pointsGroupsModalData.selectedPointsGroup && (
                <PointsGroupFormModal
                    selectedPointsGroup={pointsGroupsModalData.selectedPointsGroup}
                    isDialogOpen={!!pointsGroupsModalData.selectedPointsGroup}
                    isDelete={pointsGroupsModalData.isDelete}
                    removeTrigger
                    onCloseDialog={resetPointsGroupData}
                    onSubmit={() => {
                        resetPointsGroupData();
                        refetch();
                    }}
                />
            )}
            <DataTable
                columns={columns}
                data={pointsGroupData}
                pagination={pagination}
                search={search}
                semantics={{
                    title: formatMessage(parkingManagementMessages.pointsGroups),
                    entryName: formatMessage(parkingManagementMessages.pointsGroup),
                    entryNames: formatMessage(parkingManagementMessages.pointsGroups),
                }}
                isError={isError}
                isLoading={isLoading}
                refetch={refetch}
                components={{
                    newEntry: PointsGroupFormModal,
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
        </>
    );
}

export default PointsGroupTable;
